import {
  ClippingCapability,
  ObjectPropertiesPanelCapability,
  SelectionActionsCapability,
} from '@trimble/web3d-capabilities';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { ObjectSelectionToolbar } from './ObjectSelectionToolbar';
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  Show,
} from 'solid-js';
import { Web3DViewerWrapper } from '@trimble/utilities';

export interface ObjectSelectionToolbarContainerProps {
  viewerWrapper: Web3DViewerWrapper;
  selectionSyncCoordinator: SelectionSyncCoordinator;
  selectionActionsCapability: SelectionActionsCapability;
  clippingCapability?: ClippingCapability;
  propertiesPanelCapability?: ObjectPropertiesPanelCapability;
  /** CSS classes to forward to the toolbar (e.g. padding overrides) */
  customClasses?: string;
  /** If true, the toolbar will be rendered in dark mode regardless of the system theme. */
  darkMode?: boolean;
}

export const ObjectSelectionToolbarContainer: Component<
  ObjectSelectionToolbarContainerProps
> = (props) => {
  const [colorPickerOpen, setColorPickerOpen] = createSignal(false);

  const selectionCount = createMemo(() => {
    const counts = props.selectionSyncCoordinator.selectionCountByViewer();
    if (!counts) return 0;

    return counts.get(props.viewerWrapper.viewer) ?? 0;
  });

  const selectedEntities = createMemo(() => {
    const selectedByViewer = props.selectionSyncCoordinator.selectedByViewer();

    return (
      selectedByViewer.get(props.viewerWrapper.viewer) ??
      new Map<string, number[]>()
    );
  });

  createEffect(() => {
    props.selectionActionsCapability.setSelection(selectedEntities());
  });

  createEffect(() => {
    props.selectionActionsCapability.selection();
    setColorPickerOpen(false);
  });

  const handleClip = async (): Promise<void> => {
    const clip = props.clippingCapability;
    if (!clip) return;

    const selection = selectedEntities();
    if (selection.size === 0) return;

    await clip.addClipBoxFromSelection(selection);
  };

  return (
    <Show when={selectionCount() > 0}>
      <ObjectSelectionToolbar
        selectionCount={selectionCount()}
        customClasses={props.customClasses}
        darkMode={props.darkMode}
        onFit={() => void props.selectionActionsCapability.fitToView()}
        onClip={() => void handleClip()}
        colorPicker={{
          open: colorPickerOpen(),
          onToggle: setColorPickerOpen,
          initialOpacity: props.selectionActionsCapability.initialOpacity(),
          onChange: (c) => props.selectionActionsCapability.setColor(c),
          onOpacityChange: (o) =>
            props.selectionActionsCapability.setOpacity(o),
          onReset: () => void props.selectionActionsCapability.resetMaterials(),
        }}
        visibility={{
          onHideSelection: () =>
            void props.selectionActionsCapability.hideSelection(),
          onShowOnlySelected: () =>
            void props.selectionActionsCapability.showOnlySelected(),
          onShowAll: () => void props.selectionActionsCapability.showAll(),
          isHideSelectionActive:
            props.selectionActionsCapability.isHideSelectionActive(),
          isShowOnlySelectedActive:
            props.selectionActionsCapability.isShowOnlySelectedActive(),
          onIsolateLayers: () =>
            void props.selectionActionsCapability.isolateLayers(),
          onUnisolateLayers: () =>
            void props.selectionActionsCapability.unisolateLayers(),
        }}
        properties={
          props.propertiesPanelCapability
            ? {
                onToggle: () => void props.propertiesPanelCapability?.toggle(),
                isActive: props.propertiesPanelCapability.isOpen(),
              }
            : undefined
        }
      />
    </Show>
  );
};
